// Fase 5 — Estoque e Compras (mocked prototype)
import React from 'react';
import { Package, Boxes, ArrowRightLeft, ClipboardList, Warehouse, Truck, FileText, ShoppingCart, Receipt, Send, TrendingUp, AlertTriangle, DollarSign, PackageCheck } from 'lucide-react';
import { MockPageShell, MockKpi, MockBar, MockPie, MockLine, ChartCard, StatusBadge, MockToolbar, MockTable, EmptyStateBox } from '../../components/MockUI';
import { mockProducts, mockSuppliers, genSeries, genPie } from '../../lib/mockData';
import { Button } from '../../components/ui/button';

const bc = (label) => [{ label: 'Estoque', to: '/estoque' }, { label }];

export function EstoqueDashboard() {
  return <MockPageShell title="Dashboard de Estoque e Compras" subtitle="Movimentações, saldo e indicadores"
    breadcrumbs={[{ label: 'Estoque' }]}
    kpis={[
      <MockKpi key="1" icon={Package} label="Produtos ativos" value="1.284" sub="Cadastrados" />,
      <MockKpi key="2" icon={Warehouse} label="Valor em estoque" value="R$ 892.4K" tone="success" />,
      <MockKpi key="3" icon={AlertTriangle} label="Abaixo do mínimo" value="42" tone="critical" />,
      <MockKpi key="4" icon={ShoppingCart} label="Pedidos abertos" value="17" tone="warn" />,
    ]}
    charts={[
      <ChartCard key="1" title="Consumo mensal" subtitle="Últimos 6 meses"><MockBar data={genSeries(80, 60)} /></ChartCard>,
      <ChartCard key="2" title="Estoque por categoria"><MockPie data={genPie(['Lubrificantes', 'Filtros', 'Freios', 'Pneus', 'Motor', 'Suspensão'])} /></ChartCard>,
    ]}
  />;
}

const prodCols = [
  { key: 'code', label: 'Código', mono: true }, { key: 'name', label: 'Produto' },
  { key: 'category', label: 'Categoria' }, { key: 'unit', label: 'UN' },
  { key: 'stock', label: 'Estoque', align: 'right' }, { key: 'min', label: 'Mínimo', align: 'right' },
  { key: 'cost', label: 'Custo', align: 'right' },
];
export function Produtos() {
  return <MockPageShell title="Produtos" subtitle="Catálogo mestre" breadcrumbs={bc('Produtos')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Novo produto</Button>}
    extra={<MockToolbar searchPlaceholder="Buscar por código, nome ou categoria..." />}
    table={<MockTable columns={prodCols} rows={mockProducts.map(p => ({...p, cost: `R$ ${p.cost.toFixed(2)}`}))} />}
  />;
}

export function Categorias() {
  const rows = ['Lubrificantes', 'Filtros', 'Freios', 'Motor', 'Suspensão', 'Pneus', 'Elétrica', 'Carroceria']
    .map((n, i) => ({ code: `CAT-${String(i + 1).padStart(3, '0')}`, name: n, count: Math.round(50 + Math.random() * 200), active: 'Ativa' }));
  return <MockPageShell title="Categorias de Produtos" breadcrumbs={bc('Categorias')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova categoria</Button>}
    extra={<MockToolbar />}
    table={<MockTable columns={[{ key: 'code', label: 'Código', mono: true }, { key: 'name', label: 'Nome' }, { key: 'count', label: 'Produtos', align: 'right' }, { key: 'active', label: 'Status', status: true }]} rows={rows} statusMap={{ Ativa: 'green' }} />}
  />;
}

export function Movimentacoes() {
  const rows = Array.from({ length: 8 }, (_, i) => ({
    date: `${10 - i}/02/2026`, doc: `MV-${5000 + i}`, product: mockProducts[i % mockProducts.length]?.name,
    kind: i % 3 === 0 ? 'Entrada' : i % 3 === 1 ? 'Saída' : 'Ajuste', qty: (Math.random() * 20 | 0) + 1, warehouse: i % 2 === 0 ? 'Central' : 'Filial',
  }));
  const map = { Entrada: 'green', Saída: 'amber', Ajuste: 'blue' };
  return <MockPageShell title="Movimentações" subtitle="Entradas, saídas e ajustes"
    breadcrumbs={bc('Movimentações')}
    kpis={[
      <MockKpi key="1" icon={ArrowRightLeft} label="Movs. do mês" value="1.842" />,
      <MockKpi key="2" icon={TrendingUp} label="Entradas" value="642" tone="success" />,
      <MockKpi key="3" icon={ArrowRightLeft} label="Saídas" value="1.128" tone="warn" />,
      <MockKpi key="4" icon={Boxes} label="Ajustes" value="72" tone="info" />,
    ]}
    extra={<MockToolbar />}
    table={<MockTable columns={[{ key: 'date', label: 'Data' }, { key: 'doc', label: 'Doc', mono: true }, { key: 'product', label: 'Produto' }, { key: 'kind', label: 'Tipo', status: true }, { key: 'qty', label: 'Qtd', align: 'right' }, { key: 'warehouse', label: 'Almox.' }]} rows={rows} statusMap={map} />}
  />;
}

export function Inventario() {
  return <MockPageShell title="Inventário" subtitle="Contagens cíclicas e periódicas" breadcrumbs={bc('Inventário')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Iniciar contagem</Button>}
    kpis={[
      <MockKpi key="1" icon={ClipboardList} label="Contagens abertas" value="3" tone="warn" />,
      <MockKpi key="2" icon={PackageCheck} label="Concluídas (2026)" value="12" tone="success" />,
      <MockKpi key="3" icon={AlertTriangle} label="Divergências abertas" value="18" tone="critical" />,
    ]}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5"><h3 className="font-semibold mb-3">Contagens em andamento</h3>
      <ul className="space-y-2 text-sm">
        {[
          { code: 'INV-2026-003', name: 'Almoxarifado Central — Zona A', progress: 68 },
          { code: 'INV-2026-004', name: 'Filial Canoas', progress: 34 },
          { code: 'INV-2026-005', name: 'Peças de Motor', progress: 92 },
        ].map(i => (
          <li key={i.code} className="flex items-center gap-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
            <span className="font-mono text-xs">{i.code}</span>
            <span className="flex-1">{i.name}</span>
            <div className="w-32 h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden"><div className="h-full bg-brand" style={{ width: `${i.progress}%` }} /></div>
            <span className="text-xs w-10 text-right">{i.progress}%</span>
          </li>
        ))}
      </ul>
    </div>}
  />;
}

export function Almoxarifado() {
  const rows = [
    { code: 'ALM-001', name: 'Almoxarifado Central - Porto Alegre', responsible: 'Marcelo Rodrigues', items: 842, value: 'R$ 512K', active: 'Ativo' },
    { code: 'ALM-002', name: 'Filial Canoas - Estoque de Passagem', responsible: 'Roberto Almeida', items: 315, value: 'R$ 189K', active: 'Ativo' },
    { code: 'ALM-003', name: 'Garagem Freeway - Consumo Rápido', responsible: 'Carlos Silva', items: 127, value: 'R$ 68K', active: 'Ativo' },
  ];
  return <MockPageShell title="Almoxarifado" subtitle="Gestão de locais físicos de estoque" breadcrumbs={bc('Almoxarifado')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Novo almoxarifado</Button>}
    table={<MockTable columns={[{ key: 'code', label: 'Código', mono: true }, { key: 'name', label: 'Nome' }, { key: 'responsible', label: 'Responsável' }, { key: 'items', label: 'Itens', align: 'right' }, { key: 'value', label: 'Valor', align: 'right' }, { key: 'active', label: 'Status', status: true }]} rows={rows} statusMap={{ Ativo: 'green' }} />}
  />;
}

export function Fornecedores() {
  return <MockPageShell title="Fornecedores" breadcrumbs={bc('Fornecedores')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Novo fornecedor</Button>}
    extra={<MockToolbar />}
    table={<MockTable columns={[{ key: 'name', label: 'Razão social' }, { key: 'cnpj', label: 'CNPJ', mono: true }, { key: 'category', label: 'Categoria' }, { key: 'contact', label: 'Contato' }, { key: 'status', label: 'Status', status: true }]} rows={mockSuppliers} statusMap={{ Ativo: 'green', Inativo: 'red' }} />}
  />;
}

export function Solicitacoes() {
  const rows = [
    { id: 'SC-127', requester: 'Marcelo Rodrigues', dept: 'Manutenção', items: 4, value: 'R$ 3.240,00', status: 'Aguardando aprovação', date: '08/02/2026' },
    { id: 'SC-126', requester: 'Roberto Almeida', dept: 'Operações', items: 2, value: 'R$ 890,00', status: 'Aprovada', date: '07/02/2026' },
    { id: 'SC-125', requester: 'Carlos Silva', dept: 'Frota', items: 1, value: 'R$ 12.400,00', status: 'Convertida em pedido', date: '05/02/2026' },
    { id: 'SC-124', requester: 'Ana Paula Souza', dept: 'Manutenção', items: 6, value: 'R$ 5.680,00', status: 'Rejeitada', date: '03/02/2026' },
  ];
  return <MockPageShell title="Solicitações de Compra" breadcrumbs={bc('Solicitações')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova solicitação</Button>}
    table={<MockTable columns={[{ key: 'id', label: 'Nº', mono: true }, { key: 'requester', label: 'Solicitante' }, { key: 'dept', label: 'Setor' }, { key: 'items', label: 'Itens', align: 'right' }, { key: 'value', label: 'Valor', align: 'right' }, { key: 'status', label: 'Status', status: true }, { key: 'date', label: 'Data' }]} rows={rows} statusMap={{ 'Aguardando aprovação': 'amber', Aprovada: 'blue', 'Convertida em pedido': 'green', Rejeitada: 'red' }} />}
  />;
}

export function Cotacoes() {
  const rows = [
    { id: 'CT-089', request: 'SC-127', suppliers: 4, best: 'Bosch Brasil - R$ 3.120,00', status: 'Aguardando escolha', deadline: '12/02/2026' },
    { id: 'CT-088', request: 'SC-125', suppliers: 3, best: 'Michelin - R$ 11.500,00', status: 'Aprovada', deadline: '06/02/2026' },
  ];
  return <MockPageShell title="Cotações"
    breadcrumbs={bc('Cotações')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova cotação</Button>}
    table={<MockTable columns={[{ key: 'id', label: 'Nº', mono: true }, { key: 'request', label: 'SC', mono: true }, { key: 'suppliers', label: 'Fornecedores', align: 'right' }, { key: 'best', label: 'Melhor oferta' }, { key: 'status', label: 'Status', status: true }, { key: 'deadline', label: 'Prazo' }]} rows={rows} statusMap={{ 'Aguardando escolha': 'amber', Aprovada: 'green' }} />}
  />;
}

export function Pedidos() {
  const rows = [
    { id: 'PC-234', supplier: 'Bosch Brasil', items: 8, value: 'R$ 14.320,00', status: 'Em trânsito', delivery: '15/02/2026' },
    { id: 'PC-233', supplier: 'Michelin', items: 4, value: 'R$ 11.560,00', status: 'Recebido parcial', delivery: '10/02/2026' },
    { id: 'PC-232', supplier: 'Total Lubrificantes', items: 24, value: 'R$ 3.480,00', status: 'Recebido', delivery: '06/02/2026' },
    { id: 'PC-231', supplier: 'Fram do Brasil', items: 12, value: 'R$ 2.870,00', status: 'Cancelado', delivery: '-' },
  ];
  return <MockPageShell title="Pedidos de Compra" breadcrumbs={bc('Pedidos')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Novo pedido</Button>}
    kpis={[
      <MockKpi key="1" icon={ShoppingCart} label="Pedidos abertos" value="17" />,
      <MockKpi key="2" icon={Truck} label="Em trânsito" value="6" tone="info" />,
      <MockKpi key="3" icon={PackageCheck} label="Recebidos (mês)" value="42" tone="success" />,
      <MockKpi key="4" icon={DollarSign} label="Valor total aberto" value="R$ 89.4K" />,
    ]}
    table={<MockTable columns={[{ key: 'id', label: 'Pedido', mono: true }, { key: 'supplier', label: 'Fornecedor' }, { key: 'items', label: 'Itens', align: 'right' }, { key: 'value', label: 'Valor', align: 'right' }, { key: 'status', label: 'Status', status: true }, { key: 'delivery', label: 'Entrega' }]} rows={rows} statusMap={{ 'Em trânsito': 'blue', 'Recebido parcial': 'amber', Recebido: 'green', Cancelado: 'red' }} />}
  />;
}

export function Recebimentos() {
  const rows = [
    { id: 'RC-321', order: 'PC-233', date: '10/02/2026', items: 4, receiver: 'Marcelo Rodrigues', status: 'Conferido' },
    { id: 'RC-320', order: 'PC-232', date: '06/02/2026', items: 24, receiver: 'Roberto Almeida', status: 'Conferido' },
    { id: 'RC-319', order: 'PC-230', date: '02/02/2026', items: 3, receiver: 'Ana Paula Souza', status: 'Divergência' },
  ];
  return <MockPageShell title="Recebimentos" breadcrumbs={bc('Recebimentos')}
    table={<MockTable columns={[{ key: 'id', label: 'Nº', mono: true }, { key: 'order', label: 'Pedido', mono: true }, { key: 'date', label: 'Data' }, { key: 'items', label: 'Itens', align: 'right' }, { key: 'receiver', label: 'Recebedor' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Conferido: 'green', Divergência: 'red' }} />}
  />;
}

export function NotasFiscais() {
  const rows = [
    { id: 'NF-8934', supplier: 'Bosch Brasil', kind: 'Entrada', value: 'R$ 14.320,00', date: '10/02/2026', status: 'Processada' },
    { id: 'NF-8933', supplier: 'Michelin', kind: 'Entrada', value: 'R$ 11.560,00', date: '08/02/2026', status: 'Processada' },
    { id: 'NF-8932', supplier: 'Total Lubrificantes', kind: 'Entrada', value: 'R$ 3.480,00', date: '06/02/2026', status: 'Aguardando' },
  ];
  return <MockPageShell title="Notas Fiscais" breadcrumbs={bc('Notas Fiscais')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Importar XML</Button>}
    table={<MockTable columns={[{ key: 'id', label: 'Chave', mono: true }, { key: 'supplier', label: 'Emitente' }, { key: 'kind', label: 'Tipo' }, { key: 'value', label: 'Valor', align: 'right' }, { key: 'date', label: 'Emissão' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Processada: 'green', Aguardando: 'amber' }} />}
  />;
}

export function Transferencias() {
  const rows = [
    { id: 'TR-045', from: 'Central-POA', to: 'Filial Canoas', items: 8, date: '09/02/2026', status: 'Em trânsito' },
    { id: 'TR-044', from: 'Central-POA', to: 'Garagem Freeway', items: 3, date: '07/02/2026', status: 'Concluída' },
  ];
  return <MockPageShell title="Transferências entre Almoxarifados" breadcrumbs={bc('Transferências')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova transferência</Button>}
    table={<MockTable columns={[{ key: 'id', label: 'Nº', mono: true }, { key: 'from', label: 'De' }, { key: 'to', label: 'Para' }, { key: 'items', label: 'Itens', align: 'right' }, { key: 'date', label: 'Data' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ 'Em trânsito': 'blue', Concluída: 'green' }} />}
  />;
}

export function Relatorios() {
  const items = [
    'Curva ABC de consumo', 'Giro de estoque', 'Consumo por veículo', 'Produtos abaixo do mínimo',
    'Análise de fornecedores', 'Compras por período', 'Cotações vs. Pedidos', 'Divergências de recebimento',
    'Movimentações por almoxarifado', 'Custo médio ponderado',
  ];
  return <MockPageShell title="Relatórios de Estoque e Compras" breadcrumbs={bc('Relatórios')}
    extra={<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{items.map((r, i) => (
      <div key={i} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 flex items-center gap-3 card-hover">
        <div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><FileText className="h-5 w-5" /></div>
        <div className="flex-1"><p className="font-medium text-sm">{r}</p><p className="text-xs text-neutral-500">Exportar em PDF ou Excel</p></div>
        <Button size="sm" variant="outline">Gerar</Button>
      </div>
    ))}</div>}
  />;
}
